import type { Extended, Layers, Scope } from "./types";

/**
 * THE SETTINGS STORE — the `FrontOfficeSettings` rows, one per language.
 *
 * This is the seam that will let us switch to the real routes without touching
 * the panel: the panel only ever knows `layersFor`, `write` and `clear`.
 *
 * ⚠️ WHY WE DO NOT WRITE TO THE REAL DATABASE YET.
 * `PUT /settings/fo` currently requires NO specific right: `useHandler` only
 * checks authentication and domain membership. Any registered participant can
 * therefore rewrite an event's configuration. Wiring the panel to it before the
 * `CUSTOMIZATIONS_FO_V2` guard exists server-side would make that hole
 * convenient to reach.
 *
 * So the mechanism is complete and testable today against a local store; moving
 * to the API will be a change of implementation, not of shape.
 */

const STORAGE_KEY = "gk-fo-v2-settings";

/** Rows, indexed by language id. */
export type Rows = Record<number, Extended>;

/** Deep merge, identical to the server's: flat keys, and `typosV2` per typology. */
export function merge(base: Extended, incoming: Extended): Extended {
  const out: Extended = { ...base };
  for (const [key, value] of Object.entries(incoming)) {
    if (key !== "typosV2") {
      out[key] = value;
      continue;
    }
    const before = base.typosV2 ?? {};
    const after = (value ?? {}) as Record<string, Record<string, unknown>>;
    const typos: Record<string, Record<string, unknown>> = { ...before };
    for (const [typoId, keys] of Object.entries(after)) {
      typos[typoId] = { ...(before[typoId] ?? {}), ...keys };
    }
    out.typosV2 = typos;
  }
  return out;
}

/** The two rows the cascade consults for a given scope. */
export function layersFor(rows: Rows, scope: Scope): Layers {
  const defaultLanguage = rows[scope.defaultLanguageId] ?? {};
  // Reading the reference language means reading the VERY SAME row: returning two
  // distinct objects would skew provenance ("override" where there is none).
  const language =
    scope.languageId === scope.defaultLanguageId
      ? defaultLanguage
      : (rows[scope.languageId] ?? {});
  return { defaultLanguage, language };
}

export function load(seed: Rows): Rows {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...seed };
    const stored = JSON.parse(raw) as Rows;
    // Merge ONTO the seed, never replace it: a setting added to the catalog later
    // keeps its seeded value instead of vanishing.
    const out: Rows = { ...seed };
    for (const [id, extended] of Object.entries(stored)) {
      out[Number(id)] = merge(out[Number(id)] ?? {}, extended);
    }
    return out;
  } catch {
    // Storage unavailable (private browsing, quota): serve the seed rather than
    // bringing the application down.
    return { ...seed };
  }
}

/**
 * ═══ LES RÉGLAGES QUI N'APPARTIENNENT À AUCUNE LANGUE ═══
 *
 * Quelles langues l'événement offre, et laquelle sert de référence : ces deux-là ne
 * se traduisent pas, ils décrivent la configuration elle-même. `remote.ts` les lit
 * donc STRUCTURELLEMENT, en balayant les lignes jusqu'à en trouver une qui les
 * porte (voir `structural`).
 *
 * ⚠️ ET C'EST CE QUI LES RENDAIT IMMODIFIABLES. La lecture prend la PREMIÈRE ligne
 * qui porte la clé ; l'écriture, elle, pose la valeur sur la ligne de la langue
 * AFFICHÉE. Sur un événement dont la ligne française dit déjà « EN », un
 * organisateur qui lit l'anglais et choisit « Français » écrit sur la ligne
 * anglaise… que la lecture ne consulte jamais, puisqu'elle s'arrête à la française.
 * Le sélecteur revenait donc à sa valeur d'avant, sans un mot. Vécu sur l'événement
 * 408 : impossible de changer la langue de référence.
 *
 * La règle, maintenant : une valeur structurelle est UNIQUE. L'écrire quelque part,
 * c'est l'effacer partout ailleurs — sinon deux lignes se contredisent et c'est
 * l'ordre des identifiants de langue qui tranche, ce qui n'est le choix de personne.
 */
export const STRUCTURAL_KEYS: ReadonlySet<string> = new Set([
  "FO_MEETING_V2_LANGUAGES_SHOW",
  "FO_MEETING_V2_LANGUAGE_DEFAULT",
  /**
   * ⚠️ LE FLUX VIDÉO EST UNE PROPRIÉTÉ DE L'ÉVÉNEMENT, pas de la langue. Il n'y a
   * qu'une caméra dans la salle. Ces deux clés se LISENT déjà en balayant les
   * lignes (`useStructuralSetting`, née de l'événement 408 dont la référence était
   * l'anglais et l'adresse sur la ligne française) ; sans elles ici, l'écriture
   * partait dans la portée courante et la lecture ne la retrouvait pas : adresse
   * visible dans le panneau, lecteur absent sur la page. C'est exactement le défaut
   * que cette liste a été écrite pour fermer.
   */
  "FO_MEETING_V2_LIVE_SOURCE",
  "FO_MEETING_V2_LIVE_URL",
  /* Même raison : une seule carte SIM, donc un seul numéro, quelle que soit la langue. */
  "FO_MEETING_V2_LIVE_SMS_NUMBER",
]);

export function write(
  rows: Rows,
  entry: { languageId: number; extended: Extended },
): Rows {
  const next: Rows = {
    ...rows,
    [entry.languageId]: merge(rows[entry.languageId] ?? {}, entry.extended),
  };

  const structural = Object.keys(entry.extended).filter((key) =>
    STRUCTURAL_KEYS.has(key),
  );

  for (const key of structural) {
    for (const [id, row] of Object.entries(next)) {
      if (Number(id) === entry.languageId || !row || !(key in row)) continue;
      const without: Extended = { ...row };
      delete without[key];
      next[Number(id)] = without;
    }
  }

  persist(next);
  return next;
}

/**
 * Removes a key at the current scope so the inherited value takes over again.
 * We DELETE the key rather than writing `undefined` — merging would otherwise
 * reintroduce it with an empty value, which is not the same thing.
 */
export function clear(rows: Rows, scope: Scope, key: string): Rows {
  const current = rows[scope.languageId];
  if (!current) return rows;

  const copy: Extended = { ...current };
  if (scope.typoId) {
    const typos = { ...(copy.typosV2 ?? {}) };
    const target = { ...(typos[String(scope.typoId)] ?? {}) };
    delete target[key];
    typos[String(scope.typoId)] = target;
    copy.typosV2 = typos;
  } else {
    delete copy[key];
  }

  const next: Rows = { ...rows, [scope.languageId]: copy };
  persist(next);
  return next;
}

function persist(rows: Rows): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(rows));
  } catch {
    /* the write will not survive a reload; the screen stays correct */
  }
}

export function reset(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* nothing to do */
  }
}

/**
 * Reads a value straight from ONE language's row, ignoring the cascade.
 *
 * The panel almost never wants this — a resolved value is what a participant
 * sees. Translation does: it needs to know which languages are still SILENT on a
 * key, and an inherited value would look like an answer.
 */
export function ownValue(
  rows: Rows,
  languageId: number,
  typoId: number | null,
  key: string,
): unknown {
  const extended = rows[languageId];
  if (!extended) return undefined;
  const layer = typoId ? (extended.typosV2?.[String(typoId)] ?? {}) : extended;
  return (layer as Record<string, unknown>)[key];
}
